// Synthetic demo fleet for the live, backend-free demo build.
//
// Produces fleet data in the exact cloud `/api/nodes` shape (see
// fleet-map/hooks/useNodeFetching.js for the contract) plus the per-node
// payloads the realtime WebSocket enrichment expects
// (fleet-map/hooks/useRealtimeEnrichment.js). All values are a function of a
// controllable simulated clock, so the demo animates live in the browser with
// no backend — and the on-screen controls (see install.js) can scrub the time
// of day or change how fast time runs.
//
// The simulated "day" loops every DAY_MS of sim-time. Solar ramps up midday and
// charges the batteries, load climbs into an evening peak, the batteries
// discharge, and a genset covers the peak — then everything recharges overnight.

export const DAY_MS = 4 * 60 * 1000; // sim-ms per full day at speed 1×

// ── controllable simulated clock ──────────────────────────────────────────
// demoNowMs() is a pure function of Date.now() given the current anchors, so it
// can be called from many places (the /api/nodes shim, the WS tick, the control
// panel) without double-counting. No module-level timer runs.
let _speed = 2;          // time multiplier (0 = paused)
let _paused = false;
let _baseRealMs = Date.now();
let _baseSimMs = (10.5 / 24) * DAY_MS; // open mid-morning (solar ramping, batteries charging)

export function demoNowMs() {
  if (_paused) return _baseSimMs;
  return _baseSimMs + (Date.now() - _baseRealMs) * _speed;
}
export function setSpeed(mult) {
  _baseSimMs = demoNowMs();   // re-anchor so time stays continuous across a speed change
  _baseRealMs = Date.now();
  _speed = Math.max(0, mult);
  _paused = _speed === 0;
}
export function setSimHour(h) {
  const hh = (((h % 24) + 24) % 24);
  _baseSimMs = (hh / 24) * DAY_MS;
  _baseRealMs = Date.now();
}
export function getSimHour() {
  return ((demoNowMs() % DAY_MS) / DAY_MS) * 24;
}
export function getSpeed() { return _speed; }
export function isPaused() { return _paused; }

const EVENT_ID = 'demo-event-1';
const EVENT_NAME = 'Live Demo Event';
const DEPLOYMENT_ID = 'demo-fleet';

// ── deterministic helpers ────────────────────────────────────────────────
function hash01(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}
const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));
const gauss = (x, mu, s) => Math.exp(-Math.pow((x - mu) / s, 2));
const round = (x, n = 0) => { const p = 10 ** n; return Math.round(x * p) / p; };

function wiggle(now, seed) {
  return 1 + 0.018 * Math.sin(now / 3100 + seed * 6.3) + 0.009 * Math.sin(now / 870 + seed * 11.1);
}
function simHour(now) {
  return ((now % DAY_MS) / DAY_MS) * 24;
}
function solarFactor(h) {
  return Math.max(0, Math.sin((Math.PI * (h - 6.5)) / 11));
}
function loadFactor(h) {
  const evening = Math.max(gauss(h, 22, 3.0), gauss(h + 24, 22, 3.0));
  const afternoon = 0.4 * gauss(h, 16.5, 4.2);
  return clamp(0.12 + 0.9 * evening + afternoon, 0.1, 1);
}
function socBase(h) {
  return 60 + 30 * Math.cos((2 * Math.PI * (h - 15)) / 24); // 30..90
}
function gensetRunning(h) {
  return loadFactor(h) > 0.7 && socBase(h) < 50;
}
function phases(total, now, seed) {
  const a = total * (0.34 + 0.01 * Math.sin(now / 2600 + seed));
  const b = total * (0.33 + 0.01 * Math.sin(now / 2900 + seed + 2));
  const c = total - a - b;
  return [a, b, c];
}

// ── fleet topology (generic event site; open ground, not a named venue) ───
const NODES = [
  { id: 'node-main', name: 'Main Stage', lat: 33.72205, lng: -116.22555,
    systems: [
      { sid: 'pro6010-main-1', type: 'gx', invKw: 30, pvKw: 22, loadKw: 26 },
      { sid: 'acuvim-main-1', type: 'acuvim', loadKw: 240 },
    ] },
  { id: 'node-east', name: 'East Stage', lat: 33.71985, lng: -116.22305,
    systems: [
      { sid: 'pro505-east-1', type: 'gx', invKw: 15, pvKw: 10, loadKw: 14 },
      { sid: 'acuvim-east-1', type: 'acuvim', loadKw: 180 },
    ] },
  { id: 'node-amphi', name: 'Amphitheatre', lat: 33.72155, lng: -116.22765,
    systems: [
      { sid: 'moxion-amphi-1', type: 'moxion', invKw: 75, loadKw: 60 },
      { sid: 'acuvim-amphi-1', type: 'acuvim', loadKw: 150 },
    ] },
  { id: 'node-west', name: 'West Field', lat: 33.71895, lng: -116.22625,
    systems: [
      { sid: 'fronius-solar-1', type: 'fronius', pvKw: 50 },
      { sid: 'pro4010-west-1', type: 'gx', invKw: 20, pvKw: 14, loadKw: 16 },
    ] },
  { id: 'node-yard', name: 'Generator Yard', lat: 33.71775, lng: -116.22185,
    systems: [
      { sid: 'leyden-yard-1', type: 'leyden', invKw: 100, loadKw: 80 },
      { sid: 'dse-genset-1', type: 'dse', genKw: 120 },
    ] },
  { id: 'node-vendor', name: 'Vendor Row', lat: 33.72325, lng: -116.22355,
    systems: [
      { sid: 'pro515-vendor-1', type: 'gx', invKw: 15, pvKw: 8, loadKw: 13 },
      { sid: 'acuvim-vendor-1', type: 'acuvim', loadKw: 120 },
    ] },
];

// ── per-system value builders ─────────────────────────────────────────────
function buildSystem(cfg, now, dataTs) {
  const h = simHour(now);
  const seed = hash01(cfg.sid) * 12;
  const w = wiggle(now, seed);
  const solar = solarFactor(h);
  const load = loadFactor(h);
  const base = {
    system_id: cfg.sid,
    portal_id: cfg.sid,
    type: cfg.type,
    data_updated_at: dataTs,
    stale: false,
    online: true,
    event_id: EVENT_ID,
    alerts: [],
    alerts_count: 0,
  };

  if (cfg.type === 'gx') {
    const soc = clamp(socBase(h) + (seed - 6) * 1.2, 6, 99);
    const pv = solar * cfg.pvKw * 1000 * w;
    const demand = load * cfg.loadKw * 1000 * w;
    const net = demand - pv;
    const pout = net > 0 ? net : 0;
    const pin = net < 0 ? net : 0;
    const [l1, l2, l3] = phases(pout, now, seed);
    return {
      ...base,
      soc: Math.round(soc * 10) / 10,
      voltage: Math.round((50.5 + soc * 0.035 + 0.2 * Math.sin(now / 4000 + seed)) * 10) / 10,
      pout: Math.round(pout),
      pin: Math.round(pin),
      pv_power: Math.round(pv),
      ac_out_power: Math.round(pout),
      dc_system_power: Math.round((pv - pout) * 0.04),
      mode: net > 0 ? 'Inverter' : pv > demand ? 'Charger' : 'PassThru',
      ac_out_l1: Math.round(l1), ac_out_l2: Math.round(l2), ac_out_l3: Math.round(l3),
      temperature_sensors: { 0: { temperature: Math.round(28 + load * 16 + 2 * Math.sin(now / 5000 + seed)), type: 0 } },
      relays: { 0: false, 1: pout > cfg.invKw * 400 },
      pv_chargers: {},
    };
  }

  if (cfg.type === 'acuvim') {
    const p = load * cfg.loadKw * 1000 * w;
    const v = 277 + 1.5 * Math.sin(now / 3500 + seed);
    const [i1, i2, i3] = phases(p / v, now, seed);
    return {
      ...base,
      va: Math.round(v * 10) / 10, vb: Math.round((v - 0.6) * 10) / 10, vc: Math.round((v + 0.5) * 10) / 10,
      ia: Math.round(i1 * 10) / 10, ib: Math.round(i2 * 10) / 10, ic: Math.round(i3 * 10) / 10,
      vavg: Math.round(v * 10) / 10,
      iavg: Math.round(((i1 + i2 + i3) / 3) * 10) / 10,
      p: Math.round(p),
    };
  }

  if (cfg.type === 'fronius') {
    const ac = solar * cfg.pvKw * 1000 * w;
    const v = 277 + 1.2 * Math.sin(now / 3300 + seed);
    const [i1, i2, i3] = phases(ac / v, now, seed);
    return {
      ...base,
      pout: Math.round(ac),
      pv_power: Math.round(ac),
      va: Math.round(v * 10) / 10, vb: Math.round((v + 0.4) * 10) / 10, vc: Math.round((v - 0.5) * 10) / 10,
      ia: Math.round(i1 * 10) / 10, ib: Math.round(i2 * 10) / 10, ic: Math.round(i3 * 10) / 10,
      dc_voltage: Math.round((585 + 8 * solar) * 10) / 10,
      dc_current: Math.round((ac / 585) * 10) / 10,
      energy_total_wh: Math.round(2_450_000 + ((now / 1000) % 86400) * 0.6),
    };
  }

  if (cfg.type === 'moxion') {
    const soc = clamp(socBase(h) + (seed - 6) * 1.0, 8, 98);
    const demand = load * cfg.loadKw * 1000 * w;
    const dc = demand * 1.04;
    const [l1, l2, l3] = phases(demand, now, seed);
    const v = 277 + 1.3 * Math.sin(now / 3700 + seed);
    const [i1, i2, i3] = phases(demand / v, now, seed);
    return {
      ...base,
      soc: Math.round(soc * 10) / 10,
      bms_soc: Math.round(soc * 10) / 10,
      voltage: Math.round((720 + soc * 0.4) * 10) / 10,
      power: Math.round(dc),
      pcs_power: Math.round(demand),
      ac_out_l1: Math.round(l1), ac_out_l2: Math.round(l2), ac_out_l3: Math.round(l3),
      va: Math.round(v * 10) / 10, vb: Math.round((v - 0.5) * 10) / 10, vc: Math.round((v + 0.4) * 10) / 10,
      ia: Math.round(i1 * 10) / 10, ib: Math.round(i2 * 10) / 10, ic: Math.round(i3 * 10) / 10,
    };
  }

  if (cfg.type === 'dse') {
    const running = gensetRunning(h);
    const out = running ? cfg.genKw * 1000 * (0.55 + 0.35 * load) * w : 0;
    const v = running ? 277 + 1.0 * Math.sin(now / 2500 + seed) : 0;
    const [i1, i2, i3] = running ? phases(out / v, now, seed) : [0, 0, 0];
    return {
      ...base,
      pout: Math.round(out),
      power: Math.round(out),
      engine_rpm: running ? Math.round(1800 + 6 * Math.sin(now / 1500 + seed)) : 0,
      gen_frequency: running ? Math.round((60 + 0.08 * Math.sin(now / 1200 + seed)) * 100) / 100 : 0,
      coolant_temp: running ? Math.round(83 + 3 * Math.sin(now / 6000 + seed)) : 41,
      oil_pressure: running ? Math.round(345 + 8 * Math.sin(now / 4000 + seed)) : 0,
      voltage_l1: Math.round(v * 10) / 10, voltage_l2: Math.round((v - 0.5) * 10) / 10, voltage_l3: Math.round((v + 0.4) * 10) / 10,
      current_l1: Math.round(i1 * 10) / 10, current_l2: Math.round(i2 * 10) / 10, current_l3: Math.round(i3 * 10) / 10,
    };
  }

  if (cfg.type === 'leyden') {
    const soc = clamp(socBase(h) + (seed - 6) * 0.9, 8, 98);
    const demand = load * cfg.loadKw * 1000 * w;
    const dcv = 812 + soc * 0.5;
    const v = 277 + 1.1 * Math.sin(now / 3100 + seed);
    const [i1, i2, i3] = phases(demand / v, now, seed);
    const [gi1, gi2, gi3] = phases((demand * 0.2) / v, now, seed + 3);
    return {
      ...base,
      soc: Math.round(soc * 10) / 10,
      dc_voltage: Math.round(dcv * 10) / 10,
      battery_current: Math.round((demand / dcv) * 10) / 10,
      pout: Math.round(demand),
      dc_power: Math.round(demand * 1.03),
      ems_power: Math.round(demand),
      grid_power: Math.round(-demand * 0.2),
      grid_frequency: Math.round((60 + 0.05 * Math.sin(now / 1800 + seed)) * 100) / 100,
      load_frequency: Math.round((60 + 0.04 * Math.sin(now / 2000 + seed)) * 100) / 100,
      load_pf: Math.round((0.95 + 0.02 * Math.sin(now / 5000 + seed)) * 100) / 100,
      cell_vmin: Math.round((3.21 + soc * 0.0012) * 1000) / 1000,
      cell_vmax: Math.round((3.34 + soc * 0.0009) * 1000) / 1000,
      va: Math.round(v * 10) / 10, vb: Math.round((v - 0.5) * 10) / 10, vc: Math.round((v + 0.4) * 10) / 10,
      ia: Math.round(i1 * 10) / 10, ib: Math.round(i2 * 10) / 10, ic: Math.round(i3 * 10) / 10,
      grid_va: Math.round(v * 10) / 10, grid_vb: Math.round((v - 0.4) * 10) / 10, grid_vc: Math.round((v + 0.3) * 10) / 10,
      grid_ia: Math.round(gi1 * 10) / 10, grid_ib: Math.round(gi2 * 10) / 10, grid_ic: Math.round(gi3 * 10) / 10,
    };
  }

  return base;
}

function buildNode(cfg, now) {
  const dataTs = Math.floor(now / 1000);
  const systems = cfg.systems.map((s) => buildSystem(s, now, dataTs));
  const battery = systems.find((s) => s.type === 'gx' || s.type === 'moxion' || s.type === 'leyden') || systems[0];
  const acuvim = systems.find((s) => s.type === 'acuvim');
  return {
    node_id: cfg.id,
    system_id: battery?.system_id || cfg.id,
    host_system_id: battery?.system_id || null,
    deployment_id: DEPLOYMENT_ID,
    event_id: EVENT_ID,
    event_updated_at: dataTs,
    source: 'edge',
    version: 'v1.3.0',
    addresses: [],
    node_url: null,
    online: true,
    systems,
    soc: battery?.soc ?? null,
    voltage: battery?.voltage ?? null,
    pin: battery?.pin ?? null,
    pout: battery?.pout ?? battery?.pcs_power ?? null,
    pv_power: battery?.pv_power ?? null,
    mode: battery?.mode ?? null,
    location: cfg.name,
    latitude: cfg.lat,
    longitude: cfg.lng,
    gps_source: 'manual',
    last_seen_at: dataTs,
    data_updated_at: dataTs,
    alerts: [],
    alerts_count: 0,
    acuvim_vavg: acuvim?.vavg ?? null,
    acuvim_iavg: acuvim?.iavg ?? null,
    acuvim_p: acuvim?.p ?? null,
    acuvim_updated_at: acuvim ? dataTs : null,
    node_health: null,
  };
}

// ── public API used by the install shim ───────────────────────────────────
export function computeFleet(nowMs) {
  const now = nowMs == null ? demoNowMs() : nowMs;
  return NODES.map((cfg) => buildNode(cfg, now));
}

export function computeWsSnapshot(nowMs) {
  const now = nowMs == null ? demoNowMs() : nowMs;
  const nodes = {};
  for (const node of NODES.map((cfg) => buildNode(cfg, now))) {
    nodes[node.node_id] = {
      node_id: node.node_id,
      event_id: node.event_id,
      ts: Math.floor(now),
      version: node.version,
      last_seen_at: node.last_seen_at,
      systems: node.systems,
    };
  }
  return { type: 'snapshot', nodes };
}

// Smooth (no-wiggle) power decomposition for energy integration.
function systemPowers(cfg, h) {
  const solar = solarFactor(h);
  const load = loadFactor(h);
  if (cfg.type === 'gx') {
    const pv = solar * cfg.pvKw * 1000;
    const demand = load * cfg.loadKw * 1000;
    return { deliver_w: Math.max(0, demand - pv), pv_w: pv, gen_w: 0, load_w: demand };
  }
  if (cfg.type === 'acuvim') {
    const p = load * cfg.loadKw * 1000;
    return { deliver_w: p, pv_w: 0, gen_w: 0, load_w: p };
  }
  if (cfg.type === 'fronius') {
    const pv = solar * cfg.pvKw * 1000;
    return { deliver_w: pv, pv_w: pv, gen_w: 0, load_w: 0 };
  }
  if (cfg.type === 'moxion' || cfg.type === 'leyden') {
    const demand = load * cfg.loadKw * 1000;
    return { deliver_w: demand, pv_w: 0, gen_w: 0, load_w: demand };
  }
  if (cfg.type === 'dse') {
    const g = gensetRunning(h) ? cfg.genKw * 1000 * (0.55 + 0.35 * load) : 0;
    return { deliver_w: g, pv_w: 0, gen_w: g, load_w: 0 };
  }
  return { deliver_w: 0, pv_w: 0, gen_w: 0, load_w: 0 };
}

// Synthetic `/api/reports/live/<event>` — per-system accumulated energy + an
// event-level by-source split (solar / generator / battery), integrated from
// the start of the simulated day to the current sim time. Responds to the
// time/speed controls (scrub forward → more accumulated energy).
const FUEL_KWH_PER_GAL = 13.5; // diesel genset, typical
const DIESEL_PRICE = 4.25;
const CO2_KG_PER_GAL = 10.18;

export function computeLiveReport(now = demoNowMs()) {
  const hNow = simHour(now);
  const STEP = 0.05; // hours
  const sysAccum = {};
  for (const node of NODES) for (const s of node.systems) sysAccum[s.sid] = { energy_wh: 0, pv_energy_wh: 0 };

  let totalLoadWh = 0, pvServedWh = 0, genServedWh = 0, battServedWh = 0, pvGenWh = 0;
  for (let t = 0; t < hNow; t += STEP) {
    let fleetLoadW = 0, fleetPvGenW = 0, fleetGenW = 0;
    for (const node of NODES) for (const s of node.systems) {
      const p = systemPowers(s, t);
      sysAccum[s.sid].energy_wh += p.deliver_w * STEP;
      sysAccum[s.sid].pv_energy_wh += p.pv_w * STEP;
      fleetLoadW += p.load_w;
      fleetPvGenW += p.pv_w;
      fleetGenW += p.gen_w;
    }
    const pvServed = Math.min(fleetLoadW, fleetPvGenW);
    const genServed = Math.min(fleetGenW, Math.max(0, fleetLoadW - pvServed));
    const battServed = Math.max(0, fleetLoadW - pvServed - genServed);
    totalLoadWh += fleetLoadW * STEP;
    pvServedWh += pvServed * STEP;
    genServedWh += genServed * STEP;
    battServedWh += battServed * STEP;
    pvGenWh += fleetPvGenW * STEP;
  }

  const liveSys = {};
  for (const n of computeFleet(now)) for (const s of n.systems) liveSys[s.system_id] = s;

  const systems = {};
  for (const node of NODES) for (const s of node.systems) {
    const acc = sysAccum[s.sid];
    const live = liveSys[s.sid] || {};
    const isGen = s.type === 'dse';
    const isPvSrc = s.type === 'fronius';
    const isBatt = s.type === 'gx' || s.type === 'moxion' || s.type === 'leyden';
    const isLoad = s.type === 'acuvim';
    const curW = isLoad ? (live.p || 0) : (live.pout || live.pcs_power || 0);
    const gal = isGen ? 0 : (acc.energy_wh / 1000) / FUEL_KWH_PER_GAL;
    systems[s.sid] = {
      system_id: s.sid,
      system_type: s.type,
      energy_role: isGen ? 'generator' : isPvSrc ? 'pv' : isBatt ? 'bess' : 'load',
      ac_input_source: isGen ? 'generator' : isBatt ? 'battery' : isPvSrc ? 'pv' : null,
      location: node.name,
      energy_wh: Math.round(acc.energy_wh),
      current_power_w: Math.round(curW),
      pv_energy_wh: Math.round(acc.pv_energy_wh),
      pv_power_w: Math.round(live.pv_power || (isPvSrc ? live.pout : 0) || 0),
      is_current_location: true,
      period_start: null, period_end: null, joined_at: null,
      unit_token: null, unit_label: s.sid,
      fuel_savings: {
        fuel_saved_gal: round(gal, 1),
        co2_avoided_kg: round(gal * CO2_KG_PER_GAL, 1),
        cost_saved: round(gal * DIESEL_PRICE, 2),
        fuel_type: 'diesel',
      },
      linked_sources: isBatt ? { grid: [], pv: ['fronius-solar-1'], gen: ['dse-genset-1'] } : { grid: [], pv: [], gen: [] },
    };
  }

  const totalGal = (pvServedWh + battServedWh) / 1000 / FUEL_KWH_PER_GAL;
  return {
    event_id: EVENT_ID,
    duration_seconds: Math.round(hNow * 3600),
    systems,
    total_energy_wh: Math.round(totalLoadWh),
    total_pv_energy_wh: Math.round(pvGenWh),
    total_grid_energy_wh: 0,
    total_gen_energy_wh: Math.round(genServedWh),
    total_battery_energy_wh: Math.round(battServedWh),
    event_summary: {
      total_delivered_wh: Math.round(totalLoadWh),
      grid_wh: 0,
      pv_wh: Math.round(pvServedWh),
      gen_wh: Math.round(genServedWh),
      direct_to_load_wh: { pv: Math.round(pvServedWh) },
    },
    load_attribution: {
      grid_wh: 0,
      pv_wh: Math.round(pvServedWh),
      gen_wh: Math.round(genServedWh),
      battery_wh: Math.round(battServedWh),
      unassigned_wh: 0,
      battery_reattribution: { grid_wh: 0, pv_wh: Math.round(battServedWh * 0.6), gen_wh: Math.round(battServedWh * 0.4) },
    },
    total_fuel_saved_gal: round(totalGal, 1),
    total_co2_avoided_kg: round(totalGal * CO2_KG_PER_GAL, 1),
    total_cost_saved: round(totalGal * DIESEL_PRICE, 2),
    computed_at: new Date().toISOString(),
    schema_version: 2,
  };
}

export const DEMO_EVENT = {
  event_id: EVENT_ID,
  event_name: EVENT_NAME,
  window_id: EVENT_ID,
  name: EVENT_NAME,
  deployment_id: DEPLOYMENT_ID,
  active: true,
};

export const DEMO_CAPABILITIES = {
  context: 'cloud',
  role: 'admin',
  portal: false,
  api_key: 'demo',
  node_id: null,
  deployment_id: DEPLOYMENT_ID,
  features: { mesh_protocol: false },
  deployments: [{ deployment_id: DEPLOYMENT_ID, name: 'Demo Fleet' }],
  endpoints: {},
  demo: true,
};
